import { n as lowerMedian } from "./median-DmaPzIe-.js";
import { t as geodesicAngleRad } from "./geodesic-angle-DbbTJqSa.js";
import { quat } from "gl-matrix";
//#region ../src/ar/qr/qr-pose-aggregation.ts
/**
* QR pose aggregation — sliding-window pose stabilization
* (docs `2026-06-16-0858-qr-pose-stabilization-sliding-window-followup.md`).
*
* Pure, device-free, store-free robust aggregation of a STATIC fiducial's pose
* across a short sliding window of detections. A QR is static in the world, so
* the last N world poses should be (nearly) identical — disagreement is noise,
* which robust aggregation removes. The aggregated pose is what drives the
* high-weight GPS vote and the debug overlay, instead of a raw single-frame pose
* whose ROTATION can swing (the on-device symptom this targets).
*
* Three layers, smallest first:
* - {@link averageRotation} — Option R1: angle-thresholded inlier set + hemisphere-
*   aligned quaternion average. The real new work; translation already had
*   {@link medianQrPosition} but rotation had no counterpart. Quaternion double
*   cover (`q` ≡ `−q`) is handled explicitly — the single most common bug here.
* - {@link aggregateQrPose} — per-axis median position + R1 rotation, reporting
*   the spreads + inlier count the stability gate needs.
* - {@link evaluateQrPoseStability} — the `unknown → measuring → stable` lifecycle
*   over the last `window` poses, mirroring the size lifecycle. The pose is only
*   exposed (for the vote / overlay) once `stable`.
*
* Operates on plain `Pose` lists (no slice import) so BOTH the framework
* controller and the demo controller can reuse it, and it is unit-testable
* without a store. The slice selectors `selectStableQrPose` /
* `selectQrPoseStability` wrap these over a marker's ring buffer.
*
* INVARIANT: the caller must feed RAW per-detection world poses here. Never feed
* a previously-aggregated pose back in — the window would average its own output
* and collapse toward whatever it first locked onto (a feedback loop that defeats
* outlier rejection). The `qrDetected` ring buffer stores raw poses for exactly
* this reason.
*
* @see ./qr-pose.ts — the `Pose` type and the (raw) pose solve this aggregates.
* @see ../state/qr-detected-slice.ts — `selectStableQrPose` / `selectQrPoseStability`.
*/
const RAD2DEG = 180 / Math.PI;
const DEG2RAD = Math.PI / 180;
/** Default inlier half-angle (deg) — start ~12°, confirm on device. */
const DEFAULT_ROTATION_INLIER_ANGLE_DEG = 12;
function toGl(q) {
	const g = quat.fromValues(q[0], q[1], q[2], q[3]);
	return quat.normalize(g, g);
}
/**
* Average the samples within `thresholdRad` of `ref`. Each inlier is flipped
* into `ref`'s hemisphere (sign of the dot) BEFORE the linear average so the
* double cover cannot cancel the sum; the normalized linear average equals the
* Markley eigenvector mean for a tight cluster and needs no eigensolver.
* Returns `null` when no sample is within threshold.
*/
function meanOfInliers(samples, ref, thresholdRad) {
	const inliers = [];
	for (const s of samples) if (geodesicAngleRad(s, ref) <= thresholdRad) inliers.push(s);
	if (inliers.length === 0) return null;
	const acc = quat.fromValues(0, 0, 0, 0);
	for (const s of inliers) {
		const sign = quat.dot(s, ref) < 0 ? -1 : 1;
		acc[0] += sign * s[0];
		acc[1] += sign * s[1];
		acc[2] += sign * s[2];
		acc[3] += sign * s[3];
	}
	const mean = quat.normalize(quat.create(), acc);
	let maxAngleRad = 0;
	for (const s of inliers) maxAngleRad = Math.max(maxAngleRad, geodesicAngleRad(s, mean));
	return {
		mean,
		inlierCount: inliers.length,
		maxAngleRad
	};
}
/**
* Pick the densest sample as the reference: the one with the most OTHER samples
* within the inlier threshold. For a single rigid pose, consensus-based outlier
* rejection degenerates to this mode-finding, and it is robust to the latest
* sample being an outlier.
*/
function bestReference(samples, thresholdRad) {
	let best = samples[0];
	let bestCount = -1;
	for (const cand of samples) {
		let count = 0;
		for (const s of samples) if (geodesicAngleRad(s, cand) <= thresholdRad) count++;
		if (count > bestCount) {
			bestCount = count;
			best = cand;
		}
	}
	return best;
}
/**
* Robust mean of a set of rotations (Option R1). Picks a reference (mode-finding,
* or `options.reference`), rejects samples beyond the angular threshold, averages
* the inliers, then does ONE refit pass against the provisional mean. Returns
* `null` for an empty input.
*/
function averageRotation(quats, options = {}) {
	if (quats.length === 0) return null;
	const thresholdRad = (options.inlierAngleDeg ?? 12) * DEG2RAD;
	const samples = quats.map(toGl);
	const provisional = meanOfInliers(samples, options.reference ? toGl(options.reference) : bestReference(samples, thresholdRad), thresholdRad);
	if (!provisional) return null;
	const refined = meanOfInliers(samples, provisional.mean, thresholdRad) ?? provisional;
	const m = refined.mean;
	const s = m[3] < 0 ? -1 : 1;
	return {
		quat: [
			s * m[0],
			s * m[1],
			s * m[2],
			s * m[3]
		],
		inlierCount: refined.inlierCount,
		maxAngleDeg: refined.maxAngleRad * RAD2DEG
	};
}
function medianPosition(positions) {
	return [
		lowerMedian(positions.map((p) => p[0])),
		lowerMedian(positions.map((p) => p[1])),
		lowerMedian(positions.map((p) => p[2]))
	];
}
/** Max absolute per-axis deviation from `center` across all positions. */
function translationSpread(positions, center) {
	let max = 0;
	for (const p of positions) max = Math.max(max, Math.abs(p[0] - center[0]), Math.abs(p[1] - center[1]), Math.abs(p[2] - center[2]));
	return max;
}
/**
* Aggregate a window of raw poses into one filtered pose + the spreads the
* stability gate consumes. Returns `null` for an empty window.
*/
function aggregateQrPose(poses, options = {}) {
	if (poses.length === 0) return null;
	const positions = poses.map((p) => p.position);
	const position = medianPosition(positions);
	const rotation = averageRotation(poses.map((p) => p.rotation), options);
	if (!rotation) return null;
	return {
		pose: {
			position,
			rotation: rotation.quat
		},
		translationSpreadM: translationSpread(positions, position),
		rotationSpreadDeg: rotation.maxAngleDeg,
		inlierCount: rotation.inlierCount
	};
}
const UNKNOWN_STABILITY = {
	status: "unknown",
	pose: null,
	translationSpreadM: 0,
	rotationSpreadDeg: 0,
	inlierCount: 0,
	sampleCount: 0
};
/**
* Evaluate the pose-stability lifecycle over the last `window` of `poses`
* (oldest→newest). The returned `pose` is the filtered aggregate whenever there
* is data, but it is only the gate's responsibility (`status === 'stable'`) to
* decide when a consumer may TRUST it for the vote — `selectStableQrPose` reads
* the pose only in the `stable` state.
*/
function evaluateQrPoseStability(poses, options = {}) {
	const { window = 8, minObservations = 5, maxTranslationSpreadM = .03, maxRotationSpreadDeg = 5 } = options;
	if (poses.length === 0) return { ...UNKNOWN_STABILITY };
	const windowPoses = poses.length > window ? poses.slice(poses.length - window) : poses;
	const agg = aggregateQrPose(windowPoses, options);
	if (!agg) return { ...UNKNOWN_STABILITY };
	return {
		status: windowPoses.length >= minObservations && agg.translationSpreadM <= maxTranslationSpreadM && agg.rotationSpreadDeg <= maxRotationSpreadDeg ? "stable" : "measuring",
		pose: agg.pose,
		translationSpreadM: agg.translationSpreadM,
		rotationSpreadDeg: agg.rotationSpreadDeg,
		inlierCount: agg.inlierCount,
		sampleCount: windowPoses.length
	};
}
//#endregion
export { evaluateQrPoseStability as i, aggregateQrPose as n, averageRotation as r, DEFAULT_ROTATION_INLIER_ANGLE_DEG as t };
